import { match, P } from 'ts-pattern';
import { LOGIN_PATH, saveRedirectTarget } from './redirect-after-login';

/**
 * Base da surplus-api, **já com o prefixo `/api`** — diferente do manager, que
 * concatena o `/api` por conta própria. O default sem o prefixo fazia todo
 * request cair em 404 quando não havia `.env.local`, e o self-service traduz
 * 404 da sessão como "link expirado": em dev o link parecia sempre vencido.
 */
const BASE_URL =
  import.meta.env.VITE_SURPLUS_API_URL ?? 'http://localhost:8001/api';

const TOKEN_KEY = 'clearance-token';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * O `code` de um `detail: {code, message}` da surplus-api, se houver.
 *
 * É o jeito de distinguir duas recusas de mesmo status — um 409 de "termos não
 * aceitos" e um 409 de "loja fechada" — sem comparar texto de mensagem.
 */
export function apiErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as { detail?: { code?: unknown } } | null;
  const code = body?.detail?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * O texto da recusa, venha o `detail` como objeto ou como texto.
 *
 * `ApiError.message` só aproveita o `detail.message` do formato objeto. Um
 * `HTTPException(409, "…")` chega com o `detail` em texto, e aí a `message`
 * vira o `statusText` — "Conflict", ou vazio em HTTP/2 — no lugar da frase do
 * backend. `null` quando não há frase a mostrar (o 422 traz uma lista).
 */
export function apiErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  const text = match(detail)
    .with(P.string, (value) => value)
    .with({ message: P.string }, ({ message }) => message)
    .otherwise(() => '');
  return text.trim() || null;
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * `false` **apenas** no próprio login (`lib/auth.ts`): é o único request cujo
   * 401 significa "senha errada", e não "sua sessão acabou". Sem essa exceção,
   * errar a senha derrubaria o usuário para a tela em que ele já está.
   */
  auth?: boolean;
}

/**
 * 401 durante o uso — sessão expirada, revogada, ou conta desativada (o papel
 * é resolvido a cada request no backend, então desativar tem efeito imediato).
 *
 * Fechado o app, este passa a ser o caminho comum, não a exceção: antes ele
 * virava um `ApiError` genérico e a tela mostrava erro cru.
 *
 * O reload duro é deliberado. Ele descarta o cache do TanStack Query junto com
 * a sessão — navegar por dentro do router deixaria em memória dados de uma
 * sessão que acabou, prontos para reaparecer na tela do próximo usuário do
 * mesmo browser.
 */
function handleUnauthorized(): void {
  setToken(null);
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;
  saveRedirectTarget();
  window.location.assign(LOGIN_PATH);
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, headers, ...rest } = options;

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body !== undefined && { 'Content-Type': 'application/json' }),
    ...(headers as Record<string, string>),
  };

  if (auth) {
    const token = getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  const response = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && auth) {
    handleUnauthorized();
  }
  // 403 NÃO limpa a sessão: o usuário está autenticado, só não tem permissão
  // para este recurso (SUPPLIER, por exemplo). Deslogá-lo esconderia a causa e
  // o mandaria fazer login de novo para chegar ao mesmo 403. Propaga adiante
  // como `ApiError`, para a tela decidir o que dizer.

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      (typeof parsed === 'object' &&
        parsed !== null &&
        'detail' in parsed &&
        typeof (parsed as { detail: unknown }).detail === 'object' &&
        (parsed as { detail: { message?: string } }).detail?.message) ||
      response.statusText ||
      'Erro na requisição';
    throw new ApiError(String(message), response.status, parsed);
  }

  return parsed as T;
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
